import React from 'react';
import { useRegControls, regLinkHidden } from '../lib/regControls';
import { Link, useNavigate } from 'react-router-dom';
import { Instagram, Music2, MapPin, Calendar, ArrowUpRight, MessageCircle, FolderOpen } from 'lucide-react';
import { LINKS, SOCIALS, CATEGORIES_DISPLAY, LOGO_URL } from '../data/links';
import { trackClick } from '../lib/api';
import Marquee from '../components/Marquee';
import Lineup from '../components/Lineup';

const iconFor = (name) => {
  const cls = 'w-5 h-5 text-[#1a1a1a]';
  if (name === 'instagram') return <Instagram className={cls} />;
  if (name === 'tiktok') return <Music2 className={cls} />;
  if (name === 'whatsapp') return <MessageCircle className={cls} />;
  if (name === 'map') return <MapPin className={cls} />;
  if (name === 'drive') return <FolderOpen className={cls} />;
  return null;
};

export default function LinktreePage() {
  const regCtl = useRegControls();
  const navigate = useNavigate();

  const handleClick = (e, link) => {
    trackClick(link);
    if (!link.external) {
      e.preventDefault();
      navigate(link.url);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#E63329]">
      {/* Top marquee */}
      <Marquee />

      <div className="max-w-xl mx-auto px-5 pt-4 pb-16">
        {/* Admin nav */}
        <div className="flex justify-end mb-1">
          <Link to="/dashboard" className="text-white/80 hover:text-white text-[11px] tracking-widest uppercase font-bold">
            Dashboard
          </Link>
        </div>

        {/* Logo */}
        <div className="flex flex-col items-center text-center">
          <img
            src={LOGO_URL}
            alt="TalesVerse"
            className="w-[240px] md:w-[300px] h-auto drop-shadow-[6px_6px_0_rgba(0,0,0,0.25)]"
          />

          <div className="mt-2">
            <span className="tv-chip" style={{ backgroundColor: '#F5C518' }}>MEMORIES MARKET</span>
          </div>

          {/* Category chips (hidden per user request) */}
        </div>

        {/* Countdown section hidden per user request */}

        {/* Venue card — clickable (opens Google Maps) */}
        <a
          href="https://maps.google.com/?q=Brickhall+Fatmawati+Jakarta"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackClick({ id: 'venue', label: 'Lokasi Venue', url: 'https://maps.google.com/?q=Brickhall+Fatmawati+Jakarta' })}
          className="block bg-white text-[#1a1a1a] border-[2.5px] border-[#1a1a1a] rounded-xl mt-8 p-4 shadow-[5px_5px_0_#1a1a1a] hover:-translate-y-0.5 hover:shadow-[7px_7px_0_#1a1a1a] transition-transform"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#ffe3df] border-2 border-[#1a1a1a] flex items-center justify-center flex-shrink-0">
              <MapPin className="w-4 h-4 text-[#E63329]" />
            </div>
            <div className="font-semibold text-[14px] flex-1">The Brickhall Fatmawati, Jakarta</div>
            <ArrowUpRight className="w-4 h-4 text-black/50" />
          </div>
          <div className="flex items-center gap-3 mt-2.5">
            <div className="w-9 h-9 rounded-full bg-[#ffe3df] border-2 border-[#1a1a1a] flex items-center justify-center flex-shrink-0">
              <Calendar className="w-4 h-4 text-[#E63329]" />
            </div>
            <div className="font-semibold text-[14px]">12–13 Desember 2026</div>
          </div>
        </a>

        {/* Links */}
        <div className="mt-6 flex flex-col gap-3.5">
          {LINKS.filter((link) => !regLinkHidden(link.url, regCtl)).map((link) => {
            const isDark = link.variant === 'dark';
            const isHighlight = link.highlight;
            const bg = link.primary ? (isDark ? '#1a1a1a' : '#F5C518') : undefined;
            const textColor = isDark ? 'text-white' : 'text-[#1a1a1a]';
            const subColor = isDark ? 'text-white/60' : 'text-black/65';
            const iconBg = link.iconType === 'image'
              ? (isDark ? 'bg-[#F5C518]' : 'bg-white')
              : (link.primary ? (isDark ? 'bg-[#F5C518]' : 'bg-[#ffde4d]') : 'bg-white');
            return (
              <a
                key={link.id}
                href={link.url}
                target={link.external ? '_blank' : '_self'}
                rel={link.external ? 'noopener noreferrer' : undefined}
                onClick={(e) => handleClick(e, link)}
                className={`tv-btn block px-4 py-3.5 ${textColor} ${isHighlight ? 'ring-4 ring-[#F5C518] ring-offset-2 ring-offset-[#E63329]' : ''}`}
                style={link.primary ? { backgroundColor: bg } : undefined}
              >
                <div className="flex items-center gap-3">
                  {!(link.primary && link.iconType === 'image') && (
                    <div className={`w-12 h-12 rounded-lg border-2 border-[#1a1a1a] flex items-center justify-center flex-shrink-0 overflow-hidden ${iconBg}`}>
                      {link.iconType === 'image' ? (
                        <img src={link.iconSrc} alt="" className="w-full h-full object-contain" />
                      ) : (
                        iconFor(link.icon)
                      )}
                    </div>
                  )}
                  <div className="flex-1 text-left">
                    <div className={`font-display text-[15px] leading-tight uppercase tracking-wide ${textColor}`}>{link.label}</div>
                    <div className={`text-[11px] font-medium leading-tight mt-0.5 ${subColor}`}>{link.subtitle}</div>
                  </div>
                  <ArrowUpRight className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-white/60' : 'text-black/60'}`} />
                </div>
              </a>
            );
          })}
        </div>

        {/* Line Up (hidden per user request) */}
        {false && <Lineup />}

        {/* Socials — icon-only circles */}
        <div className="flex items-center justify-center gap-4 mt-8">
          {SOCIALS.map((s) => (            <a
              key={s.id}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick({ id: s.id, label: s.label, url: s.url })}
              aria-label={s.label}
              className="w-12 h-12 rounded-full bg-white border-[2.5px] border-[#1a1a1a] shadow-[3px_3px_0_#1a1a1a] flex items-center justify-center hover:-translate-y-0.5 hover:shadow-[4px_4px_0_#1a1a1a] transition-transform"
            >
              {s.id === 'instagram'
                ? <Instagram className="w-5 h-5 text-[#1a1a1a]" />
                : <Music2 className="w-5 h-5 text-[#1a1a1a]" />}
            </a>
          ))}
        </div>

        <div className="mt-8 text-center text-white/70 text-[10px] tracking-widest uppercase">
          © {new Date().getFullYear()} TalesVerse Official
        </div>
      </div>

      <Marquee />
    </div>
  );
}
